
import {DisplayField} from '@/lib/classes/InfoWindowContent';
import {Comp} from '@/lib/classes/InfoWindowContent/InfoWindowContent';
import {UserAction} from '@/types/commonTypes';

export type OverrideMode = 'input';

/**
 * Where an override should be written back to on the source document, if anywhere.
 * `field` is the canonical underwriting field key (e.g. `'taxes'`, `'premiseArea'`).
 */
export interface OverrideTarget {
  scope: 'portfolio' | 'building' | 'tenant';
  field: string;
  buildingId: string | null;
  tenantId: string | null;
}

export interface OverrideEntry {
  value: unknown;
  mode: OverrideMode;
  /** Absent for cells that don't round-trip to a source firestore document. */
  overrideTarget?: OverrideTarget;
  /**
   * The user-facing field label captured at edit time. Version history renders this so a row still reads correctly
   * even if the field is later renamed or the variable becomes unregistered
   */
  label?: string;
}

export interface CapExRow {
  rowId: string;
}

export interface CapExRowConfig {
  rows: CapExRow[];
  deletedRowIds: string[];
}

export interface RecordRef {
  kind: 'building' | 'portfolio';
  id: string;
  dataType: 'firestore';
}

// Fields common to every scenario document, independent of recordRef.kind and isBase.
interface ScenarioMetadataCommon {
  id: string;
  scenarioName: string;
  lastVersionedOverrides: Record<string, OverrideEntry> | null;
  deleted: Date | null;
  createdOn: UserAction;
  editedOn: UserAction;
}

/**
 * Per-building overrides carried inside any portfolio scenario (base or derived). Holds only the per-building CapEx
 * config and override map — the building's underlying data (name, location, underwritingData, comps, displayFields)
 * is snapshotted exclusively on the portfolio base scenario via PortfolioBuildingBaseSnapshot.
 */
export interface PortfolioBuildingSnapshot {
  capExRows: CapExRowConfig;
  overrides: Record<string, OverrideEntry>;
  excluded?: Date | null;
  /**
   * Building Specs display values (field label → formatted value) captured when `excluded` was set, so the
   * Summary tab's greyed-out column keeps showing what the user last saw. Removed when the building is re-included.
   */
  specSnapshot?: Record<string, string>;
}

/**
 * Base-only per-building data. The portfolio base scenario is the sole source of truth for each linked building's
 * snapshotted shape; derived scenarios carry only overrides on top.
 */
export interface PortfolioBuildingBaseSnapshot extends PortfolioBuildingSnapshot {
  name: string;
  location: {latitude: number; longitude: number} | null;
  underwritingData: Record<string, unknown>;
  comps: Comp[];
  displayFields: DisplayField[];
  /** Set when the building is soft-deleted from the scenario (e.g. unlinked or deleted via the info window). */
  deleted?: Date | null;
}

// Building scenarios. One CapExRowConfig, one overrides map.
interface BuildingScenarioShape extends ScenarioMetadataCommon {
  recordRef: RecordRef & {kind: 'building'};
  capExRows: CapExRowConfig;
  overrides: Record<string, OverrideEntry>;
}

// Portfolio derived scenarios: per-building overrides only.
interface PortfolioDerivedScenarioShape extends ScenarioMetadataCommon {
  recordRef: RecordRef & {kind: 'portfolio'};
  buildings: Record<string, PortfolioBuildingSnapshot>;
  // Portfolio-level overrides only. Per-building overrides live on buildings[bid].overrides.
  overrides: Record<string, OverrideEntry>;
}

// Portfolio base scenarios: per-building snapshots (full data + overrides). Full building snapshots recorded only on
// base scenarios. Derived scenarios reference those snapshots and layer on overrides.
interface PortfolioBaseScenarioShape extends ScenarioMetadataCommon {
  recordRef: RecordRef & {kind: 'portfolio'};
  buildings: Record<string, PortfolioBuildingBaseSnapshot>;
  overrides: Record<string, OverrideEntry>;
}

// Snapshot fields present on base scenarios (both building and portfolio).
interface BaseSnapshotFields {
  isBase: true;
  recordName: string;
  recordLocation: {latitude: number; longitude: number} | null;
  // Snapshotted in raw Firestore shape (not adapter output) so that write-back at version creation can match
  // comps/displayFields by UUID
  underwritingData: Record<string, unknown>;
  comps: Comp[];
  displayFields: DisplayField[];
}

export type BuildingScenario = BuildingBaseScenario | BuildingDerivedScenario;
export type PortfolioScenario = PortfolioBaseScenario | PortfolioDerivedScenario;

export type BuildingBaseScenario = BuildingScenarioShape & BaseSnapshotFields;
export type PortfolioBaseScenario = PortfolioBaseScenarioShape & BaseSnapshotFields;
export type BuildingDerivedScenario = BuildingScenarioShape & {isBase: false};
export type PortfolioDerivedScenario = PortfolioDerivedScenarioShape & {isBase: false};

export type BaseScenario = BuildingBaseScenario | PortfolioBaseScenario;
export type DerivedScenario = BuildingDerivedScenario | PortfolioDerivedScenario;
export type Scenario = BaseScenario | DerivedScenario;

// DTOs
export type BuildingBaseScenarioDto = Omit<BuildingBaseScenario, 'id'>;
export type PortfolioBaseScenarioDto = Omit<PortfolioBaseScenario, 'id'>;
export type BaseScenarioDto = BuildingBaseScenarioDto | PortfolioBaseScenarioDto;

export type BuildingDerivedScenarioDto = Omit<BuildingDerivedScenario, 'id'>;
export type PortfolioDerivedScenarioDto = Omit<PortfolioDerivedScenario, 'id'>;
export type DerivedScenarioDto = BuildingDerivedScenarioDto | PortfolioDerivedScenarioDto;

export type ScenarioDto = BaseScenarioDto | DerivedScenarioDto;

export const UNDERWRITING_SCENARIOS_COLLECTION = 'underwriting-scenarios' as const;
export const DEFAULT_CAPEX_ROW_COUNT = 1 as const;
